/**
 * Die Knöpfe „Ja“ und „Nein“ unter einem Beitrag des Entwicklers. (16.10.2026, Dreier-Modus)
 *
 * Martin: bittet der Schmied um ein Ja/Nein, geht das Mikro nicht immer auf — darum steht unter seinem Beitrag (Feld `frage: "ja-nein"`,
 * shared/entwickler-im-chat.ts) ein Knopfpaar. Nur Konto 1 sieht es (der Aufrufer prüft das, app.ts).
 *
 * ⚠️ Der Beitrag bleibt Fremdtext. Die Knöpfe sind Martins Hand: erst SEIN Klick sendet über den gewöhnlichen Chat-Sendeweg eine Nachricht
 *    von Martin („Ja“ / „Nein“, genau dieses Wort, nie ein Text aus dem Beitrag). Der Beitrag selbst löst nichts aus.
 * ⚠️ Doppelklick-sicher: der erste Klick sperrt beide Knöpfe SOFORT (vor dem Senden) und zeigt die Wahl; scheitert das Senden
 *    (`false`), geht das Paar wieder auf, damit Martin es noch einmal versuchen kann.
 * ⚠️ Beantwortet ist eine Frage NUR, wenn (a) Martin einen ihrer Knöpfe klickt, (b) danach eine Nachricht von Martin steht, die belegt an den
 *    Schmied ging (Vermerk „Martin → Schmied“, `.bubble-adressat[data-an="schmied"]`), oder (c) danach eine NEUERE Frage kommt (nur die
 *    jüngste bleibt klickbar). Eine Nachricht an Vespera oder ohne Vermerk — auch das automatisch geöffnete Mikro — sperrt NICHT.
 *    Gilt live, nach dem Neuladen und bei getippter Antwort gleichermaßen (`sperreBeantworteteFragen`, Reihenfolge im DOM statt Zeitstempel).
 * ⚠️ Genau zwei Knöpfe, Ja und Nein. Ein Daumen-hoch-Knopf (18.10.2026) war nur ein zweites Ja und ist am 20.10.2026 entfallen (Martin).
 * ⚠️ Gesperrt heißt sichtbar „beantwortet“ (Text + aria-label), nicht nur gedimmt.
 * ⚠️ 20.10.2026 (Martin 12:38: „Ich kann den Knopf nicht drücken … das Ja muss Bezug nehmen auf die jeweilige Frage“): Standen zwei Karten
 *    gleichzeitig, sperrte (c) die ältere SOFORT als „beantwortet“, und Martins Ja auf die neuere sperrte über (b) auch sie — ohne dass er sie
 *    je beantwortet hatte. Karten MIT Kennung (`optionen.kennung`, vom Server, shared/schmied-karte.ts) sind darum unabhängig: (b) und (c)
 *    gelten für sie nicht. Gesperrt wird so eine Karte nur durch ihren eigenen Tipp, durch eine spätere Antwortblase mit IHRER Kennung
 *    (`data-karte-antwort`, Neuladen/zweites Gerät) oder durch die Antwort des Servers („schon beantwortet“, „abgelaufen“, „unbekannt“).
 *    Der Sender schickt dann Kennung + Antwort an die Karten-Route (lib/karte-anfrage.ts), nie ein Wort über den Chat-Weg.
 *    Karten ohne Kennung (alte Beiträge, Notschalter) bleiben wie bisher.
 * ⚠️ 21.10.2026 (Martin: „wenn ein Ja/Nein offen ist, dass irgendwo im Chatverlauf direkt steht, dass man es in diesem Zusammenhang
 *    absegnet“): die Karte trägt oben eine Zeile „Offene Frage: <Kurztext>“ (der letzte Fragesatz des Beitrags, nur Text). Beantwortet
 *    heißt sie „Frage: …“. Sie ist der EINZIGE Ort für das Karten-Ja — das Aufnahme-Fenster zeigt kein Ja/Nein mehr (lib/ptt-modal.ts).
 *    Die Zeile ist reine Anzeige: sie sendet nichts, und der Sendeweg (Wort/Kennung, Empfänger) ist unverändert.
 */


export type FrageAntwort = "Ja" | "Nein";

const ANTWORTEN: readonly FrageAntwort[] = ["Ja", "Nein"];

/**
 * Der Empfänger der Antwort: IMMER der Schmied — er hat gefragt. (16.10.2026, Prüfung der Lockerung SCHMIED_BEITRAG_SPERRT)
 * Ohne die Wahl entschieden die Regeln bzw. Jev über den Adressaten; fiel das auf Vespera, hätte Martins „Ja“ eine OFFENE Rückfrage
 * Vesperas (Mac: Datei löschen o. Ä.) einlösen können. Mit `an: "schmied"` (Martins Wahl, server/adressat-jev.ts: Wahl schlägt alles)
 * antwortet Vespera auf diese Nachricht nicht und kein Werkzeug läuft.
 */
export const FRAGE_EMPFAENGER = "schmied" as const;

/** Der Server sagt: diese Karte gilt nicht mehr (schon beantwortet, abgelaufen, unbekannt) — sie wird mit diesem Text gesperrt. */
export interface FrageGesperrt {
  gesperrt: string;
}

/** Sendet Martins Antwort an `an`; `false` = nicht angekommen (Paar geht wieder auf), `{ gesperrt }` = Karte gilt nicht mehr. */
export type FrageSender = (
  antwort: FrageAntwort,
  an: typeof FRAGE_EMPFAENGER,
  /**
   * 20.10.2026: war es ein ECHTER Tipp (`isTrusted`)? Nur dann zeigt der Chat keine eigene Martin-Blase „Ja“ (die Karte zeigt den Stand);
   * ein Skript-Klick sendet wie bisher und bleibt als Blase sichtbar. Die Zeile ist in beiden Fällen dieselbe Chat-Zeile, nie ein Beleg.
   */
  echt: boolean,
) => Promise<boolean | FrageGesperrt> | boolean | FrageGesperrt;

/** Journal-Haken: Grund der Sperre („klick“, „antwort-schmied“, „neuere-frage“, „karte-antwort“) und wie viele Paare. Nur Wörter und Zahlen, nie Text. */
export type FrageSperrGrund =
  "klick" | "antwort-schmied" | "neuere-frage" | "karte-antwort";
export type FrageMelder = (grund: FrageSperrGrund, anzahl: number) => void;

/**
 * 20.10.2026 (Journal 23:04:25–26: `[frage] gesperrt grund=karte-antwort n=1` ×8 in einer Sekunde): beim Neuzeichnen des Verlaufs
 * (hydrateFromServer, renderHistoryToDom) sperrt jede Antwortblase ihre Karte einzeln — keine Schleife, aber eine Zeile je Karte.
 * Der Bündler zählt alle Meldungen eines Durchlaufs je Grund und gibt sie EINMAL weiter (`plane`: nach dem laufenden Durchlauf).
 */
export function buendleFrageMelder(
  melde: FrageMelder,
  plane: (fn: () => void) => void = (fn) => queueMicrotask(fn),
): FrageMelder {
  const offen = new Map<FrageSperrGrund, number>();
  const REIHE: readonly FrageSperrGrund[] = ["klick", "antwort-schmied", "neuere-frage", "karte-antwort"];
  return (grund, anzahl) => {
    if (anzahl <= 0) return;
    const leer = offen.size === 0;
    offen.set(grund, (offen.get(grund) ?? 0) + anzahl);
    if (!leer) return;
    plane(() => {
      const stand = new Map(offen);
      offen.clear();
      for (const g of REIHE) {
        const n = stand.get(g);
        if (n) melde(g, n);
      }
    });
  };
}

/** Der leise Bestätigungston nach angenommenem Tipp (20.10.2026): nur Rückmeldung, nie ein Ja; die Sperren prüft `frageTonErlaubt`. */
export type FrageQuittung = (antwort: FrageAntwort) => void;

/**
 * Darf der Bestätigungston klingen? (20.10.2026, Martin: „akustisches Feedback, damit ich weiß, dass es angenommen wurde“)
 * Nur wenn ALLES stimmt: echter Tipp, Senden angenommen (nicht `false`), kein Anruf, kein offenes Mikro-Fenster/keine Aufnahme
 * (das Mikro-Signal bleibt allein) und der Ton nicht ausgeschaltet. Die sichtbare Wahl („beantwortet: Ja“) bleibt die Hauptanzeige.
 */
export function frageTonErlaubt(z: {
  echt: boolean;
  angekommen: boolean;
  imAnruf: boolean;
  mikroOffen: boolean;
  tonAus: boolean;
  /** Der Ton-Kontext läuft (`running`): sonst käme der Ton verspätet nach einem späteren resume (iOS). */
  kontextLaeuft: boolean;
}): boolean {
  return z.echt && z.angekommen && !z.imAnruf && !z.mikroOffen && !z.tonAus && z.kontextLaeuft;
}

const KURZ_MAX = 120;

/**
 * Der Kurztext der Frage: der LETZTE Satz mit „?“ am Ende (sonst der letzte Satz), ohne Markdown-Zeichen, auf ~120 Zeichen gekürzt.
 * Nur Text, nie HTML (gesetzt über `textContent`). (18.10.2026 aus lib/ptt-frage-slot.ts, seit 21.10.2026 hier.)
 */
export function letzterFragesatz(text: string): string {
  const saetze = text
    .replace(/[*`]+/g, "")
    .replace(/^[ \t]*(?:#{1,6}|>|[-+][ \t])[ \t]*/gm, "")
    // Zeilen sind eigene Sätze (Listen, Überschriften ohne Punkt); innerhalb einer Zeile trennt das Satzende.
    .split(/\n+|(?<=[.!?…])\s+/)
    .map((x) => x.replace(/\s+/g, " ").trim())
    .filter((x) => x.length > 0);
  if (saetze.length === 0) return "";
  const satz = [...saetze].reverse().find((x) => x.endsWith("?")) ?? saetze[saetze.length - 1];
  return satz.length > KURZ_MAX ? `${satz.slice(0, KURZ_MAX - 1).trimEnd()}…` : satz;
}

/** Die sichtbare Marke vor dem Kurztext: offen oder beantwortet. */
const MARKE_OFFEN = "Offene Frage:";
const MARKE_ZU = "Frage:";

/** Zusätze zum Knopfpaar. */
export interface KartenOptionen {
  /** 20.10.2026: die Kennung der Karte (6 Hex, vom Server) — dann ist das Paar unabhängig von anderen Karten (siehe oben). */
  kennung?: string;
}

/** Das Knopfpaar (noch nicht eingehängt). `frageText` dient nur der Beschriftung für Bildschirmleser. */
export function baueFrageKnoepfe(
  frageText: string,
  sende: FrageSender,
  melde?: FrageMelder,
  quittung?: FrageQuittung,
  optionen: KartenOptionen = {},
): HTMLElement {
  const bezug = frageText.replace(/\s+/g, " ").trim().slice(0, 80);
  const kennung = optionen.kennung && /^[0-9a-f]{6}$/.test(optionen.kennung) ? optionen.kennung : null;
  const zeile = document.createElement("div");
  zeile.className = "entwickler-frage";
  zeile.setAttribute("role", "group");
  if (kennung) zeile.dataset.karte = kennung;
  zeile.setAttribute("aria-label", labelOffen(zeile));
  // Die Zeile „Offene Frage: <Kurztext>“ — zuerst, damit klar ist, WOZU Ja und Nein gehören.
  const kurz = letzterFragesatz(frageText);
  const titel = document.createElement("p");
  titel.className = "entwickler-frage-titel";
  const marke = document.createElement("span");
  marke.className = "entwickler-frage-marke";
  marke.textContent = MARKE_OFFEN;
  titel.appendChild(marke);
  if (kurz) {
    const kurzEl = document.createElement("span");
    kurzEl.className = "entwickler-frage-kurz";
    kurzEl.textContent = kurz;
    titel.append(" ", kurzEl);
  }
  zeile.appendChild(titel);
  /** EIN Sendeweg für Ja und Nein: sperrt zuerst (Doppeltipp), sendet dann das Wort an den Schmied. */
  const antworte = (antwort: FrageAntwort, grund: "klick", echt: boolean): void => {
    if (zeile.dataset.beantwortet === "1") return;
    sperre(zeile, antwort);
    melde?.(grund, 1);
    void Promise.resolve(sende(antwort, FRAGE_EMPFAENGER, echt)).then(
      (angekommen) => {
        if (angekommen === false) {
          oeffne(zeile);
          return;
        }
        // Der Server sagt: diese Karte gilt nicht mehr — gesperrt mit seinem Text, kein Bestätigungston.
        if (typeof angekommen === "object" && angekommen !== null) {
          sperre(zeile, undefined, angekommen.gesperrt);
          return;
        }
        // Nur nach angenommenem ECHTEN Tipp; ein Fehler im Ton ändert nichts am Senden (Rückmeldung, nie ein Ja).
        if (echt) {
          try {
            quittung?.(antwort);
          } catch {
            /* Ton ist Beiwerk */
          }
        }
      },
      () => oeffne(zeile),
    );
  };
  for (const antwort of ANTWORTEN) {
    const knopf = document.createElement("button");
    knopf.type = "button";
    knopf.className = `entwickler-frage-knopf entwickler-frage-${antwort === "Ja" ? "ja" : "nein"}`;
    knopf.textContent = antwort;
    knopf.setAttribute(
      "aria-label",
      bezug ? `${antwort} antworten auf: ${bezug}` : `${antwort} antworten`,
    );
    knopf.addEventListener("click", (e) => antworte(antwort, "klick", e.isTrusted === true));
    zeile.appendChild(knopf);
  }
  // Die Kennung sichtbar (klein, nach den Knöpfen): dieselbe, die der Schmied in seiner Zeile sieht — so ist klar, welche Frage gemeint ist.
  if (kennung) {
    const kennMarke = document.createElement("span");
    kennMarke.className = "entwickler-frage-kennung";
    kennMarke.textContent = `k-${kennung}`;
    kennMarke.setAttribute("aria-hidden", "true");
    zeile.appendChild(kennMarke);
  }
  return zeile;
}

/** Das Etikett eines offenen Paars — mit Kennung, wenn es eine hat. */
function labelOffen(zeile: HTMLElement): string {
  const id = zeile.dataset.karte;
  return id ? `${LABEL_OFFEN}, Frage k-${id}` : LABEL_OFFEN;
}

function knoepfeVon(zeile: HTMLElement): HTMLButtonElement[] {
  return Array.from(
    zeile.querySelectorAll<HTMLButtonElement>("button.entwickler-frage-knopf"),
  );
}

const LABEL_OFFEN = "Antwort auf die Frage des Entwicklers";

/** Sperrt beide Knöpfe; `gewaehlt` (falls bekannt) bleibt als gedrückt markiert. Sichtbarer Hinweis „beantwortet“ statt nur Dimmen. */
function sperre(zeile: HTMLElement, gewaehlt?: FrageAntwort, grundText?: string): void {
  zeile.dataset.beantwortet = "1";
  setzeMarke(zeile, MARKE_ZU);
  for (const k of knoepfeVon(zeile)) {
    k.disabled = true;
    k.setAttribute(
      "aria-pressed",
      gewaehlt && k.textContent === gewaehlt ? "true" : "false",
    );
  }
  const text = grundText ?? (gewaehlt ? `beantwortet: ${gewaehlt}` : "beantwortet");
  zeile.setAttribute("aria-label", `${labelOffen(zeile)} (${text})`);
  let hinweis = zeile.querySelector<HTMLElement>(
    ":scope > .entwickler-frage-status",
  );
  if (!hinweis) {
    hinweis = document.createElement("span");
    hinweis.className = "entwickler-frage-status";
    zeile.appendChild(hinweis);
  }
  hinweis.textContent = text;
  // Die Kennung bleibt das letzte Kind (steht hinter dem Hinweis).
  const marke = zeile.querySelector(":scope > .entwickler-frage-kennung");
  if (marke) zeile.appendChild(marke);
}

/** Offen „Offene Frage:“, gesperrt „Frage:“ — der Kurztext bleibt. */
function setzeMarke(zeile: HTMLElement, text: string): void {
  const m = zeile.querySelector<HTMLElement>(":scope > .entwickler-frage-titel > .entwickler-frage-marke");
  if (m) m.textContent = text;
}

function oeffne(zeile: HTMLElement): void {
  delete zeile.dataset.beantwortet;
  setzeMarke(zeile, MARKE_OFFEN);
  zeile.setAttribute("aria-label", labelOffen(zeile));
  zeile.querySelector(":scope > .entwickler-frage-status")?.remove();
  for (const k of knoepfeVon(zeile)) {
    k.disabled = false;
    k.removeAttribute("aria-pressed");
  }
}

/** Martins Nachricht, die belegt an den Schmied ging (Vermerk „Martin → Schmied“, lib/adressat-vermerk.ts). */
function anSchmied(m: HTMLElement): boolean {
  return (
    m.querySelector(':scope > .bubble-adressat[data-an="schmied"]') !== null
  );
}

/**
 * Sperrt, was wirklich beantwortet ist (siehe oben): Paare mit einer späteren, an den Schmied adressierten Nachricht von Martin und
 * alle Paare vor einer neueren Frage. Läuft in DOM-Reihenfolge; `melde` bekommt je Grund die Zahl NEU gesperrter Paare.
 */
export function sperreBeantworteteFragen(
  chat: ParentNode,
  melde?: FrageMelder,
): void {
  const alle = Array.from(
    chat.querySelectorAll<HTMLElement>(
      ".bubble.entwickler > .entwickler-frage, .bubble.user",
    ),
  );
  // 20.10.2026: Martins Tipp-Antwort (Marke `data-kartenantwort`, Wort Ja/Nein, belegt an den Schmied) wird still, sobald eine Schmied-Karte
  // davor sie als Antwort nimmt: die ERSTE solche Zeile zwischen dieser Karte und der nächsten. Die Karte zeigt dann die Wahl (auch nach Neuladen).
  const gewaehlt = new Map<HTMLElement, FrageAntwort>();
  alle.forEach((el, i) => {
    if (!el.classList.contains("entwickler-frage")) return;
    const naechste = alle.findIndex((m, j) => j > i && m.classList.contains("entwickler-frage"));
    const ende = naechste === -1 ? alle.length : naechste;
    const antwort = alle.slice(i + 1, ende).find((m) => m.classList.contains("user") && !m.classList.contains("is-nicht-angekommen") && anSchmied(m));
    if (antwort?.dataset.kartenantwort !== "1") return;
    const wort = antwort.dataset.text;
    if (wort !== "Ja" && wort !== "Nein") return;
    antwort.classList.add("bubble--kartenantwort");
    gewaehlt.set(el, wort);
  });
  const neu: Record<"antwort-schmied" | "neuere-frage", number> = {
    "antwort-schmied": 0,
    "neuere-frage": 0,
  };
  // 20.10.2026: nur Paare OHNE Kennung zählen als „neuere Frage“ für die alten Paare; ein Paar MIT Kennung ist unabhängig.
  let letzteFrage = -1;
  alle.forEach((el, i) => {
    if (el.classList.contains("entwickler-frage") && !el.dataset.karte) letzteFrage = i;
  });
  let karteGesperrt = 0;
  alle.forEach((el, i) => {
    if (!el.classList.contains("entwickler-frage")) return;
    const id = el.dataset.karte;
    if (id) {
      // Nur eine spätere Antwortblase mit GENAU dieser Kennung (vom Server gesetzt) beantwortet diese Karte.
      // 22.10.2026 (Martin 07:55): ALLE solchen Zeilen werden still — kam das Echo des Servers vor der Antwort der Karten-Route, standen
      // zwei da, und nur die erste war versteckt (sichtbares „Martin → Zimmermann: Ja“ unter „beantwortet: Ja“).
      const antwortBlasen = alle.slice(i + 1).filter((m) => m.classList.contains("user") && m.dataset.karteAntwort === id);
      const antwortBlase = antwortBlasen[0];
      if (!antwortBlase) return;
      // Die Karte zeigt die Wahl („beantwortet: Ja“, gewählter Knopf gedrückt) — keine zweite Blase; die Zeile bleibt im DOM und im Verlauf.
      // Auch bei schon gesperrter Karte (echter Tipp: erst gesperrt, dann kommt die Zeile) — darum VOR der Prüfung „beantwortet“.
      for (const b of antwortBlasen) b.classList.add("bubble--kartenantwort");
      if (el.dataset.beantwortet === "1") return;
      const w = antwortBlase.dataset.karteWahl;
      sperre(el, w === "Ja" || w === "Nein" ? w : undefined);
      karteGesperrt++;
      return;
    }
    if (el.dataset.beantwortet === "1") return;
    const grund =
      i < letzteFrage
        ? "neuere-frage"
        : alle
              .slice(i + 1)
              .some((m) => m.classList.contains("user") && anSchmied(m))
          ? "antwort-schmied"
          : null;
    if (!grund) return;
    sperre(el, gewaehlt.get(el));
    neu[grund]++;
  });
  if (melde) {
    for (const g of ["antwort-schmied", "neuere-frage"] as const)
      if (neu[g] > 0) melde(g, neu[g]);
    if (karteGesperrt > 0) melde("karte-antwort", karteGesperrt);
  }
}

/**
 * 20.10.2026: Martins Zeile ist der Tipp auf die Karte `kennung` (Merkmal vom Server, `metadata.karteAntwort`) — sie trägt die Kennung
 * (`data-karte-antwort`, sperrt GENAU diese Karte, `sperreBeantworteteFragen`) und die Wahl. Martin 20.10. 18:59: „Das Ja ist zu viel, weil es
 * ja schon in der Card ist“ — darum zeigt der Chat keine eigene Blase; sie wird dort versteckt, wo die Karte sie als Antwort nimmt. Nie aus Text:
 * nur aufgerufen mit dem Merkmal.
 */
export function setzeKarteVermerk(blase: HTMLElement, kennung: string, wort: string): void {
  if (!/^[0-9a-f]{6}$/.test(kennung)) return;
  blase.dataset.karteAntwort = kennung;
  if (wort === "Ja" || wort === "Nein") blase.dataset.karteWahl = wort;
}

/**
 * 22.10.2026: steht Martins Zeile zu dieser Karte schon im Chat (das Echo des Servers kam vor der Antwort der Karten-Route)? Dann zeichnet
 * der Tipp keine zweite — nur Anzeige; ob die Antwort gilt, entscheidet allein der Server.
 */
export function karteAntwortSteht(chat: ParentNode, kennung: string): boolean {
  return Array.from(chat.querySelectorAll<HTMLElement>(".bubble.user")).some((b) => b.dataset.karteAntwort === kennung);
}
